import app from 'flarum/forum/app';
import { timeline, stressBars, sitsBar, sitsColour, HOME_LABELS } from '../../common/money';
import { SCENARIO_LABELS } from '../../common/stayorgo';

/**
 * "Make your money work" on Voucha: the full result. Your number and date, under each naira path, where your money
 * sits, what borrowing would do (the bad case beside the good one), what giving pays for, and every assumption.
 * It never says what to buy.
 */
const full = (amount, currency) => {
  if (currency === 'NGN') {
    const a = Math.abs(amount);
    return (amount < 0 ? '−' : '') + '₦' + (a >= 1e6 ? (Math.round(a / 1e5) / 10).toLocaleString() + ' million' : (Math.round(a / 1000) * 1000).toLocaleString());
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(Math.round(amount / 100) * 100 || 0);
  } catch (e) {
    return currency + ' ' + Math.round(amount).toLocaleString();
  }
};
const pct = x => (Math.round(x * 1000) / 10).toString() + '%';
const signed = x => (x > 0 ? '+' : x < 0 ? '−' : '') + pct(Math.abs(x));
const RISK = {
  safe: 'Keep it safe',
  balanced: 'Balanced',
  bold: 'Bold'
};
const WHERE = {
  nigeria: 'in Nigeria',
  abroad: 'abroad',
  both: 'between both'
};
const LOAN = {
  personal: 'a personal loan',
  line: 'a line of credit',
  home: 'a loan against a home'
};

/** "about 61 (in 29 years)" or "in 29 years" */
export function when(date, age) {
  if (date === null || date === undefined) return null;
  if (date === 0) return 'already';
  return age ? 'at about ' + (age + date) + ' (in ' + date + ' years)' : 'in ' + date + ' years';
}
export function moneyResult(s) {
  const cur = s.place.currency;
  const money = v => full(v, cur);
  const reach = when(s.date, s.age);
  const late = s.stopAge && s.age && s.date !== null ? s.age + s.date - s.stopAge : null;
  const loan = s.loan;
  return m("div", {
    className: "VMoney"
  }, m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "Your number: ", money(s.number), ".", ' ', reach === 'already' ? 'Your savings already reach it.' : reach ? 'At your current pace you get there ' + reach + '.' : "At your current pace your savings don't reach it within 60 years."), m("div", {
    className: "VMoney-pic"
  }, m.trust(timeline({
    line: s.line,
    lineGood: s.lineGood,
    lineBad: s.lineBad,
    lineLoan: loan ? loan.line : null,
    number: s.number,
    currency: cur,
    date: s.date,
    age: s.age
  }))), m("p", {
    className: "VMoney-key"
  }, m("i", {
    className: "is-save"
  }), " Your savings ", m("i", {
    className: "is-band"
  }), " Good and bad runs ", loan ? [m("i", {
    className: "is-loan"
  }), ' With the loan'] : null), m("p", {
    className: "VMoney-note"
  }, "Your number is what you want to spend a year then (", money(s.spendThen), ", today's money) divided by ", Math.round(s.withdrawal * 10000) / 100, "%, the share you could take out each year without running out, spending ", WHERE[s.where], ".", ' ', s.range.good !== null && s.range.bad !== null && s.range.good !== s.range.bad ? 'With good or bad returns: between ' + s.range.good + ' and ' + s.range.bad + ' years. ' : s.range.good !== null && s.range.bad === null ? 'With good returns, ' + s.range.good + ' years; with bad ones, not within 60. ' : '', late !== null && s.stopAge ? late > 0 ? 'That is about ' + late + ' years later than you would like.' : 'That is by the age you hoped for.' : ''), s.whatIf ? m("p", {
    className: "VMoney-note"
  }, "Putting away ", pct(s.whatIf.share), " of what you take home", s.give ? ', less what you give,' : '', ' ', s.whatIf.date !== null ? 'you would get there ' + when(s.whatIf.date, s.age) + '.' : 'would still not get you there within 60 years.') : null), m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "Under each path for the naira"), m("ul", {
    className: "VMoney-paths"
  }, Object.keys(s.dates).map(k => m("li", {
    className: k === s.view ? 'is-view' : ''
  }, m("span", null, SCENARIO_LABELS[k]), m("strong", null, s.dates[k] === null ? 'Not within 60 years' : s.dates[k] === 0 ? 'Already there' : when(s.dates[k], s.age))))), m("p", {
    className: "VMoney-note"
  }, s.place.atHome ? 'Money you hold abroad gains in naira when the naira weakens, and loses when it strengthens.' : 'Money you hold in naira gains when the naira strengthens, and loses when it weakens.', ' ', "Highlighted: the path you picked.")), m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "Where your money sits"), m("div", {
    className: "VMoney-pic"
  }, m.trust(sitsBar({
    sits: s.sits
  }))), m("ul", {
    className: "VMoney-legend"
  }, Object.keys(s.sits).map(k => m("li", null, m("i", {
    style: 'background:' + sitsColour(k)
  }), " ", HOME_LABELS[k], ":", ' ', k === 'abroad' ? pct(s.risk.r) + ' a year (' + RISK[s.risk.key] + ', down about ' + pct(s.risk.drop) + ' in a bad year)' : k === 'farming' || k === 'business' ? 'no growth counted: there is no fair number for it' : pct(s.home.rates[k]) + ' a year' + (k === 'tbills' ? ', live' : ', an assumption')))), m("p", {
    className: "VMoney-note"
  }, "Returns are after inflation. Where money sits at home is split evenly between what you picked.")), loan ? m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "If you borrowed to invest at home"), m("p", null, "Borrowing ", money(loan.amount), " through ", LOAN[loan.kind], " at ", loan.rate, "% a year (", loan.rateSource, ") costs about ", pct(loan.realRate), " a year after inflation. Put into what you picked at home, here is what you would keep each year:"), m("ul", {
    className: "VMoney-paths"
  }, Object.keys(loan.kept).map(k => m("li", {
    className: k === s.view ? 'is-view' : ''
  }, m("span", null, SCENARIO_LABELS[k]), m("strong", {
    className: loan.kept[k] >= 0 ? 'is-up' : 'is-down'
  }, signed(loan.kept[k]), " a year")))), m("p", {
    className: "VMoney-note"
  }, s.place.atHome ? 'This pays only if your investment earns more than ' + pct(loan.breakEven) + ' a year after inflation.' : 'This pays only if your investment earns more than ' + pct(loan.breakEven) + ' a year after inflation, with the naira holding its value; more if it weakens.', ' ', "Over ", loan.years, " years it would ", loan.result >= 0 ? 'add about ' : 'cost you about ', money(Math.abs(loan.result)), s.date !== loan.date ? ', moving your date ' + (loan.date === null ? 'beyond 60 years' : 'to ' + (s.age ? 'about ' + (s.age + loan.date) : 'year ' + loan.date)) : ', without changing your date', "."), m("h3", {
    className: "VMoney-sub"
  }, "The bad case"), m("div", {
    className: "VMoney-pic"
  }, m.trust(stressBars({
    stress: loan.stress,
    currency: cur
  }))), m("p", {
    className: "VMoney-note"
  }, "The loan stays the same size whatever happens to ", loan.stress.what, ". If ", loan.stress.what, " falls ", pct(loan.stress.fallShare), " while you still owe it, what you bought covers less of the loan, and the rest comes from your savings.")) : null, s.give ? m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "What your giving pays for"), m("p", null, full(s.give.naira, 'NGN'), " a year", cur !== 'NGN' ? ' (about ' + money(s.give.local) + ')' : '', " pays roughly", ' ', s.give.secondary !== null ? s.give.secondary + " children's fees at a private secondary school in Lagos" : '', s.give.university !== null ? ', or ' + s.give.university + " students' federal university tuition" : '', "."), m("p", {
    className: "VMoney-note"
  }, "Fees only, from two reported examples (private school fees, September 2026; federal university tuition, 2025 session); housing, food and books are extra. It comes out of what you put away, so your date above already counts it.")) : null, m("details", {
    className: "VAssessResult-section VAssessResult-answersBox"
  }, m("summary", null, "How this was worked out"), m("ul", {
    className: "VStanding-assumptions"
  }, s.assumptions.map(a => m("li", null, a)))));
}

/** Tags to join: retirement always, and the investing tags that match the picks, only those that exist here */
export function moneyTags(s) {
  const bySlug = {
    shares: 'stocks',
    land: 'real-estate',
    farming: 'agriculture',
    business: 'business',
    tbills: 'investing'
  };
  const slugs = ['retirement', 'investing'].concat((s.home.picks || []).map(k => bySlug[k]));
  return [...new Set(slugs)].filter(Boolean).map(slug => app.store.getBy('tags', 'slug', slug)).filter(Boolean);
}
flarum.reg.add('voucha-assessments', 'forum/components/MoneyResult', { when: when,moneyResult: moneyResult,moneyTags: moneyTags, });